import { useCallback, useEffect, useRef } from "react";
import { bisector, pointer, select, timeFormat } from "d3";

const Tooltip = ({
  xScale,
  yScale,
  xAccessor,
  yAccessor,
  width,
  data,
  top,
  bottom,
  anchorEl,
  format
}: any) => {
  const ref = useRef(null);

  const drawLine = useCallback(
    (x, y) => {
      select(ref.current)
        .select(".tooltip-xLine")
        .attr("x1", x)
        .attr("x2", x)
        .attr("y1", top+5)
        .attr("y2", bottom+20);
      select(ref.current).select(".contentTitle").attr("transform", `translate(${x-30},${top})`);
    },
    [ref, top, bottom]
  );

  const drawBackground = useCallback((container) => {
    // reset background size to defaults
    // console.log(container);
    const contentBackground = select(container)
      .select(".contentBackground");
    contentBackground.attr("width", 120).attr("height", 40);

    // calculate new background size
    const tooltipContentElement = select(container).select(".content").node() as SVGElement;
    if (!tooltipContentElement) return;

    const contentSize = tooltipContentElement.getBoundingClientRect();

    contentBackground.attr("width", contentSize.width + 12).attr("height", contentSize.height + 2);
  }, []);

  const drawContent = useCallback(
    (x) => {
      const tooltipContent = select(ref.current).selectAll(".tooltipContent");
      tooltipContent.attr("transform", (cur, i, nodes) => {
        drawBackground(nodes[i]);
        const nodeWidth = (nodes[i] as SVGElement)?.getBoundingClientRect()?.width || 0;

        const translateX = nodeWidth + x > width ? x - nodeWidth - 8 : x + 8;

        return `translate(${translateX}, ${i === 0 ? top + 20 : top + 70})`;
      });

      select(ref.current)
        .select(".contentTitle")
        .text(isNaN(xScale.invert(x)) ? "" : timeFormat("%b %d, %Y")(xScale.invert(x)));
    },
    [xScale, top, width, drawBackground]
  );

  const onChangePosition = useCallback((d, i, isVisible) => {
    select(ref.current)
      .selectAll(".item-value")
      .filter((td, tIndex) => tIndex === i)
      .text(isVisible && yAccessor(d) ? format(yAccessor(d), i) : "No data");
  }, [yAccessor, format]);

  const followPoints = useCallback(
    (event) => {
      const [x] = pointer(event);
      let xDate = xScale.invert(x);
      const bisectDate = bisector((d: any) => xAccessor(d)).left;
      let baseXPos = 0;
      let baseYPos = 0;
      let hasFirstData = true;

      // draw circles on line
      select(ref.current)
        .selectAll(".tooltipLinePoint")
        .attr("transform", (cur, i) => {
          const index = bisectDate(data[i].data, xDate);
          const d = data[i].data[index];

          let isVisible = true;
          let xPos = 0;
          let yPos = 0;
          let date = xDate;

          if (d === undefined) {
            hasFirstData = i !== 0;
            isVisible = false;
            // move point out of container
            // return "translate(-1000,-100)";
          } else {
            xPos = xScale(xAccessor(d));
            yPos = yScale(i)(yAccessor(d));
          }

          if (i === 0 || !hasFirstData) {
            baseXPos = xPos;
            baseYPos = yPos;
            xDate = date;
          }

          if (Math.round(xPos) !== Math.round(baseXPos) && hasFirstData) {
            isVisible = false;
          }

          onChangePosition(d, i, isVisible);

          return isVisible
            ? `translate(${xPos}, ${yPos})`
            : "translate(-100,-100)";
        });

      drawLine(baseXPos, baseYPos);
      drawContent(baseXPos);
      // drawBackground();
    },
    [
      drawLine,
      drawContent,
      // drawBackground,
      xScale,
      yScale,
      xAccessor,
      yAccessor,
      data,
      onChangePosition,
    ]
  );

  useEffect(() => {
    select(anchorEl)
      .on("mouseout.tooltip", () => {
        select(ref.current).transition().duration(10).attr("display", "none");
      })
      .on("mouseover.tooltip", () => {
        select(ref.current).transition().duration(10).attr("display", "block");
      })
      .on("mousemove.tooltip", (event) => {
        select(ref.current)
          .selectAll(".tooltipLinePoint")
          .transition()
          .duration(10)
          .attr("display", "block");
        followPoints(event);
      });
  }, [anchorEl, followPoints]);

  if (!data.length) return null;

  return (
    <g ref={ref} display="none">
      <text className="contentTitle" fill="#95A0B7" fontSize={10} />
      <line className="tooltip-xLine" stroke="#cfcfcf" strokeWidth={0.5} />
      {/* <line className="tooltip-yLine" stroke="#5676e9" strokeWidth={1} /> */}
      {data.map(({ title, borderColor }: any, i: number) => (
        <g key={title} className="tooltipContent">
          <rect className="contentBackground" rx={1} ry={1} fill="#fff" strokeWidth={1} stroke="#f0f0f0" filter="drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.05))" />
          <g className="content" transform="translate(6,12)">
            <text fill={borderColor} fontSize="10">
              {title}
            </text>
            <g transform="translate(0, 18)">
              <text className="item-value" fill={borderColor} fontSize="13" fontWeight={"bold"} />
            </g>
          </g>
        </g>
      ))}
      {data.map(({ title, borderColor }: any) => (
        <circle className="tooltipLinePoint" r={3} key={title} fill={borderColor} opacity={0} />
      ))}
    </g>
  );
};

export default Tooltip;
